/**
 * AuthProvider
 *
 * @descrition Provider for check if a user is logged, otherwise
 * will redirect to login page (only applied for web version)
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import type { FC } from 'react';
import { usePublicEnv } from '@app/shared/contexts/PublicEnv';
import { ExternalRepository } from '@app/shared/repositories/external';
import { Constants } from '@app/shared/config/constants';
import { PROD_ENVIRONMENT } from '@appscore/web-constants/environments';
import { getWindow } from '@app/shared/utils/window';
import { isWebView } from '@app/shared/utils/webview';
import PeyaLoader from '@pedidosya/web-fenix/animations/PeyaLoader';

type Props = {
  children: React.ReactNode;
};

const AuthProvider: FC<Props> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const env = usePublicEnv();
  const service = new ExternalRepository();

  const urlRedirect = useCallback(async () => {
    try {
      const result = await service.getRedirectLogin();
      getWindow().location.href = `${result?.redirectUrl}&${Constants.ORIGIN_PATH}=${Constants.MY_ORDERS_PATH}`;
    } catch {
      setLoading(false);
    }
  }, []);

  const isValid = useMemo(() => {
    if (env?.environment !== PROD_ENVIRONMENT) {
      return true;
    }

    if (!isWebView() && !env?.userId) {
      return false;
    }

    return true;
  }, [env]);

  useEffect(() => {
    if (!isValid) {
      urlRedirect();
    } else {
      setLoading(false);
    }
    return () => {
      setLoading(false);
    };
  }, [env, urlRedirect]);

  if (loading && !isWebView()) {
    return <PeyaLoader position="center" />;
  }

  return <>{children}</>;
};

export { AuthProvider };
